<script setup>
import { useApiRequest } from '@/composables/useApiRequest'
import { useApi } from '@/utils/api'
import { useAbility } from '@casl/vue'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Product',
  },
})

const ability = useAbility()
const { loading, request } = useApiRequest()

const groups = ref([])
const totalItems = ref(0)
const page = ref(1)
const perPage = ref(20)
const deleteDialog = ref(false)
const selectedGroup = ref(null)
const snackbar = ref({ show: false, text: '', color: 'success' })

const headers = [
  { title: 'Nama Group', key: 'name', sortable: false },
  { title: 'Wajib', key: 'is_required', sortable: false, align: 'center' },
  { title: 'Multi-pilih', key: 'is_multiple', sortable: false, align: 'center' },
  { title: 'Opsi', key: 'modifiers', sortable: false, align: 'center' },
  { title: 'Status', key: 'is_active', sortable: false, align: 'center' },
  { title: 'Aksi', key: 'actions', sortable: false, align: 'center' },
]

// ── Fetch ─────────────────────────────────────────────────────────────────
const fetchGroups = async () => {
  const res = await request(() =>
    useApi('/api/v1/modifier-groups', {
      query: { page: page.value, per_page: perPage.value },
    }).get()
  )
  
  if (res) {
    const data = res?.data ?? res
    groups.value = data?.data ?? []
    totalItems.value = data?.pagination?.total ?? 0
  }
}

onMounted(fetchGroups)
watch([page], fetchGroups)

// ── Delete ─────────────────────────────────────────────────────────────────
const confirmDelete = group => {
  selectedGroup.value = group
  deleteDialog.value = true
}

const doDelete = async () => {
  const res = await request(() =>
    useApi(`/api/v1/modifier-groups/${selectedGroup.value.id}`).delete()
  )
  
  if (res) {
    showSnackbar('Modifier group berhasil dihapus')
    deleteDialog.value = false
    fetchGroups()
  }
}

const showSnackbar = (text, color = 'success') => {
  snackbar.value = { show: true, text, color }
}
</script>

<template>
  <div>
    <!-- Header -->
    <VRow class="mb-4">
      <VCol cols="12">
        <div class="d-flex align-center justify-space-between flex-wrap gap-3">
          <div>
            <h4 class="text-h5 font-weight-bold">Modifier Groups</h4>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Kelola pilihan tambahan produk (topping, level es, ukuran, dll)
            </p>
          </div>
          <VBtn
            v-if="ability.can('create', 'ModifierGroup')"
            prepend-icon="ri-add-line"
            :to="{ name: 'catalog-modifier-groups-create' }"
          >
            Tambah Group
          </VBtn>
        </div>
      </VCol>
    </VRow>

    <!-- Table -->
    <VCard>
      <VDataTableServer
        :headers="headers"
        :items="groups"
        :items-length="totalItems"
        :loading="loading"
        :items-per-page="perPage"
        v-model:page="page"
        @update:items-per-page="v => { perPage = v; page = 1; fetchGroups() }"
      >
        <template #item.is_required="{ item }">
          <VIcon
            :icon="item.is_required ? 'ri-check-line' : 'ri-close-line'"
            :color="item.is_required ? 'success' : 'medium-emphasis'"
          />
        </template>

        <template #item.is_multiple="{ item }">
          <VIcon
            :icon="item.is_multiple ? 'ri-check-line' : 'ri-close-line'"
            :color="item.is_multiple ? 'success' : 'medium-emphasis'"
          />
        </template>

        <template #item.modifiers="{ item }">
          <VChip size="small" color="secondary" variant="tonal">
            {{ item.modifiers?.length ?? 0 }} opsi
          </VChip>
        </template>

        <template #item.is_active="{ item }">
          <VChip
            :color="item.is_active ? 'success' : 'default'"
            variant="tonal"
            size="small"
          >
            {{ item.is_active ? 'Aktif' : 'Non-aktif' }}
          </VChip>
        </template>

        <template #item.actions="{ item }">
          <div class="d-flex gap-1 justify-center">
            <VBtn
              v-if="ability.can('update', 'ModifierGroup')"
              icon
              variant="text"
              size="small"
              :to="{ name: 'catalog-modifier-groups-id-edit', params: { id: item.id } }"
            >
              <VIcon icon="ri-pencil-line" />
              <VTooltip activator="parent">Edit</VTooltip>
            </VBtn>
            <VBtn
              v-if="ability.can('delete', 'ModifierGroup')"
              icon
              variant="text"
              size="small"
              color="error"
              @click="confirmDelete(item)"
            >
              <VIcon icon="ri-delete-bin-line" />
              <VTooltip activator="parent">Hapus</VTooltip>
            </VBtn>
          </div>
        </template>

        <template #no-data>
          <div class="text-center py-10">
            <VIcon icon="ri-checkbox-multiple-line" size="56" color="medium-emphasis" class="mb-3" />
            <p class="text-body-1 text-medium-emphasis">Belum ada modifier group</p>
            <VBtn
              v-if="ability.can('create', 'ModifierGroup')"
              size="small"
              variant="tonal"
              class="mt-2"
              :to="{ name: 'catalog-modifier-groups-create' }"
            >
              Tambah Group Pertama
            </VBtn>
          </div>
        </template>
      </VDataTableServer>
    </VCard>

    <!-- Delete dialog -->
    <VDialog v-model="deleteDialog" max-width="420">
      <VCard>
        <VCardText class="pt-5 pb-3 text-center">
          <VIcon icon="ri-error-warning-line" size="52" color="warning" class="mb-3" />
          <h6 class="text-h6 mb-1">Hapus Modifier Group?</h6>
          <p class="text-body-2 text-medium-emphasis">
            Group <strong>{{ selectedGroup?.name }}</strong> dan semua opsi di dalamnya
            akan dihapus permanen.
          </p>
        </VCardText>
        <VCardActions class="justify-center pb-5 gap-3">
          <VBtn variant="tonal" @click="deleteDialog = false">Batal</VBtn>
          <VBtn color="error" :loading="loading" @click="doDelete">Hapus</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VSnackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.text }}
    </VSnackbar>
  </div>
</template>
